import { useTranslation } from 'react-i18next'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import * as yup from 'yup'

import { Divider, Grid, Paper, Stack, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'

import PolicySelectionTable, { PolicyData } from '@/pages/admin/groups/components/PolicySelectionTable'
import TextInputController from '@/components/ui/inputs/TextInputController'

const apiKeyFormSchema = yup.object({
  name: yup.string(),
  policies: yup.array(yup.string().required()),
})

type ApiKeyFormValues = yup.InferType<typeof apiKeyFormSchema>

type ApiKeyFormProps = {
  initialValues?: ApiKeyFormValues
  isSubmitting?: boolean
  onSubmit?: (values: ApiKeyFormValues) => void
  policies?: PolicyData[]
  readOnly?: boolean
}

export function ApiKeyForm(props: ApiKeyFormProps) {
  const { initialValues, isSubmitting, onSubmit: onSubmitProp, policies = [], readOnly = false } = props
  const { t } = useTranslation()

  const { control, handleSubmit, setValue } = useForm<ApiKeyFormValues>({ defaultValues: initialValues ?? { name: '', policies: [] } })

  const onSubmit: SubmitHandler<ApiKeyFormValues> = (values) => {
    onSubmitProp?.(values)
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Stack spacing={4}>
        <Paper>
          <Stack direction="row" alignItems="center" justifyContent="space-between" p={3}>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography variant="h2" component="div" noWrap sx={{ fontSize: { sm: '20px', lg: '24px' }, lineHeight: '28px', fontWeight: 700 }}>
                {t('form.apiKey.name.title')}
              </Typography>
            </Stack>
          </Stack>
          <Divider />
          <Grid container rowGap={3} sx={{ p: 3 }}>
            <Grid item xs={12} md={6}>
              <TextInputController control={control} fullWidth label={t('field.name.label')} name="name" readOnly={readOnly} />
            </Grid>
          </Grid>
        </Paper>
        <Paper>
          <Stack direction="row" alignItems="center" justifyContent="space-between" p={3}>
            <Stack spacing={1} sx={{ flex: 1 }}>
              <Typography variant="h2" component="div" noWrap sx={{ fontSize: { sm: '20px', lg: '24px' }, lineHeight: '28px', fontWeight: 700 }}>
                {t('form.apiKey.policies.title')}
              </Typography>
            </Stack>
          </Stack>
          <Divider />
          <Controller
            control={control}
            name="policies"
            render={({ field }) => (
              <PolicySelectionTable
                data={policies}
                onSelectRow={(rows) => {
                  setValue(
                    'policies',
                    rows.map((row) => row.id)
                  )
                }}
                selectedRows={policies.filter((policy) => field.value?.includes(policy.id))}
                selectable={!readOnly}
              />
            )}
          />
        </Paper>
        <Stack direction="row" justifyContent="end">
          {!readOnly && (
            <LoadingButton loading={isSubmitting} type="submit" variant="contained">
              {t('common:button.submit.label')}
            </LoadingButton>
          )}
        </Stack>
      </Stack>
    </form>
  )
}

export default ApiKeyForm
